import { UseQueryResult } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";
import { uniqBy } from "lodash";
import { CustomValuesByTypeResponse, useCustomValuesByType } from "PFCore/hooks/queries/custom_fields/admin";
import { CustomValue, Id, OrderOption } from "PFTypes";
import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";

import { DEFAULT_ORDER } from "../../constants/default_order";
import { STATE_OPTIONS } from "../../constants/state_options";
import { useSkillsCustomTypeId } from "../../utils/use_skills_custom_type";

const PER_PAGE = 25;

export type SimpleOrder = Pick<OrderOption, "name" | "direction" | "text">;

export type SkillsManagementModalData = {
  skills?: CustomValue[];
};

type ModalContext = {
  data: SkillsManagementModalData;
  setData: (data: SkillsManagementModalData) => void;
};

type SidePanelContext = {
  isSidePanelOpen: boolean;
  setIsSidePanelOpen: (open: boolean) => void;
  sidePanelSkillId: Id | null;
  setSidePanelSkillId: Dispatch<SetStateAction<Id | null>>;
};

type SkillsManagementContextProvider = {
  selectedCounter: SkillCounter;
  setSelectedCounter: (tab: SkillCounter) => void;
  term: string;
  setTerm: (term: string) => void;
  skillsData: UseQueryResult<CustomValuesByTypeResponse>;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  modal: ModalContext;
  setOrder: (order?: SimpleOrder) => void;
  order?: SimpleOrder;
  orderOptions: SimpleOrder[];
  rowSelectionState: RowSelectionState;
  setRowSelectionState: Dispatch<SetStateAction<RowSelectionState>>;
  selectedSkills: CustomValue[];
  sidePanel: SidePanelContext;
};

export enum SkillCounter {
  All = "all",
  Approvals = "approvals",
  Blocked = "blocked",
  Merged = "merged"
}

export const SkillsManagementContext = createContext({} as SkillsManagementContextProvider);

export const SkillsManagementContextProvider = ({ children }) => {
  const [selectedCounter, setSelectedCounter] = useState<SkillCounter>(SkillCounter.All);
  const [orderOptions, setOrderOptions] = useState<OrderOption[]>([]);
  const [term, setTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [order, setOrder] = useState<SimpleOrder | undefined>(DEFAULT_ORDER);
  const [rowSelectionState, setRowSelectionState] = useState<RowSelectionState>({});
  const [selectedSkills, setSelectedSkills] = useState<CustomValue[]>([]);

  const [modalData, setModalData] = useState<SkillsManagementModalData>({});

  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
  const [sidePanelSkillId, setSidePanelSkillId] = useState<Id | null>(null);

  const skillCustomTypeId = useSkillsCustomTypeId();

  const skillsData = useCustomValuesByType(
    skillCustomTypeId as number,
    currentPage,
    PER_PAGE,
    {
      order,
      filters: { fields: { state: STATE_OPTIONS[selectedCounter], term } }
    },
    {}
  );

  useEffect(() => {
    setSelectedSkills((previous) =>
      uniqBy([...previous, ...(skillsData.data?.entries || [])], "id").filter(({ id }) =>
        Object.keys(rowSelectionState).includes(id.toString())
      )
    );
  }, [rowSelectionState, skillsData.data?.entries]);

  useEffect(() => {
    if (skillsData.isSuccess && orderOptions.length === 0 && skillsData.data?.meta.order.options) {
      setOrderOptions(skillsData.data.meta.order.options);
    }
  }, [orderOptions.length, skillsData.data?.meta.order.options, skillsData.isSuccess]);

  return (
    <SkillsManagementContext.Provider
      value={{
        selectedCounter,
        setSelectedCounter,
        term,
        setTerm,
        currentPage,
        setCurrentPage,
        modal: {
          data: modalData,
          setData: setModalData
        },
        skillsData,
        setOrder,
        order,
        orderOptions,
        rowSelectionState,
        setRowSelectionState,
        selectedSkills,
        sidePanel: {
          isSidePanelOpen,
          setIsSidePanelOpen,
          sidePanelSkillId,
          setSidePanelSkillId
        }
      }}
    >
      {children}
    </SkillsManagementContext.Provider>
  );
};

export const useSkillsManagementContext = () => useContext(SkillsManagementContext);
